import { memo } from 'react';

import { cn } from '@/utils/cn';
import { Info, X } from 'lucide-react';

import { HideMenuButton } from './components/HideMenuButton';
import { NavMenuItem } from './components/NavMenuItem';
import { useNavigationContext } from './context/NavigationContext';
import { useDynamicMenu } from './hooks/useDynamicMenu';
import { useTrackRecents } from './hooks/useTrackRecents';

const APP_VERSION = '2.0.0-alpha';

const VersionFooter = ({ isCollapsed }: { isCollapsed: boolean }) => (
  <div className={cn('border-border-500/40 shrink-0 border-t px-3 py-2.5', isCollapsed && 'flex justify-center')}>
    {isCollapsed ? (
      <span title={`v${APP_VERSION}`} className="text-secondary-500 hover:text-typography-200 cursor-help">
        <Info className="h-3.5 w-3.5" />
      </span>
    ) : (
      <span className="text-secondary-500 text-[10px]">v{APP_VERSION}</span>
    )}
  </div>
);

const NavigationContent = memo(function NavigationContent() {
  const { isNavCollapsed, toggleNavCollapse, isMobileOpen, closeMobileMenu } = useNavigationContext();
  const { menu: dynamicMenu, addRecent, allMenuItems } = useDynamicMenu();

  useTrackRecents(addRecent, allMenuItems);

  return (
    <>
      {isMobileOpen && <div className="bg-overlay fixed inset-0 z-40 md:hidden" onClick={closeMobileMenu} />}
      <nav
        className={cn(
          'bg-secondary-600 flex h-full flex-col overflow-x-hidden shadow-xl',
          'transition-all duration-300 ease-in-out',
          'fixed inset-y-0 left-0 z-50 md:relative md:z-auto',
          isNavCollapsed ? 'w-[68px]' : 'w-60',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
        role="navigation"
        aria-label="Main navigation"
      >
        {isMobileOpen && (
          <button type="button" className="absolute top-4 right-4 z-10 md:hidden" onClick={closeMobileMenu}>
            <X className="text-typography-200 h-5 w-5" />
          </button>
        )}
        <HideMenuButton isNavCollapsed={isNavCollapsed} toggleNavCollapse={toggleNavCollapse} />
        <div
          className={cn(
            'flex-1 overflow-x-hidden overflow-y-auto py-2',
            isNavCollapsed ? 'nav-scrollbar-collapsed px-1.5' : 'nav-scrollbar-expanded px-2'
          )}
        >
          <ul className="flex flex-col gap-0.5" role="menubar">
            {dynamicMenu.map((item, index) => {
              return <NavMenuItem key={item.text} item={item} index={index} />;
            })}
          </ul>
        </div>
        <VersionFooter isCollapsed={isNavCollapsed} />
      </nav>
    </>
  );
});

export const Navigation = () => {
  return <NavigationContent />;
};
